import React, { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { Search, TrendingUp, TrendingDown, Plus, X, BarChart3, LineChart as LineChartIcon } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = ''///process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let requestObj = JSON.parse(sessionStorage.getItem('token')) || {};
let displayName = Object.keys(requestObj).length ? requestObj['firstName'] + ' ' + requestObj['lastName'] : null

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockQuote, setStockQuote] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [myPositionsList, setMyPositionsList] = useState([]);
  const [recommendationList, setRecommendationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('line');
  const [period, setPeriod] = useState('1mo');
  const [seen, setSeen] = useState(false);
  const [signIn, setSignIn] = useState(true);
  const [tab, setTab] = useState("tab3");

  useEffect(() => {
    fetchWatchlist();
    fetchRecommendationList();
  }, []);

  function togglePop () {
      setSeen(!seen);
  };

  function toggleSignIn () {
      setSignIn(!signIn);
  };

  useEffect(() => {
    if (searchQuery.length > 0) {
      searchStocks();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const searchStocks = async () => {
    try {
      const response = await axios.get(`${API}/stocks/search?query=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching stocks:', error);
    }
  };

  const onTabChange = (value) => {
      setTab(value);
  }

  const fetchStockDetails = async (symbol, name) => {
    setLoading(true);
    try {
      const [quoteRes, historyRes] = await Promise.all([
        axios.get(`${API}/stocks/quote/${symbol}`),
        axios.get(`${API}/stocks/history/${symbol}?period=${period}`)
      ]);
      
      setSelectedStock({ symbol, name });
      setStockQuote(quoteRes.data);
      setStockHistory(historyRes.data);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch stock details';
      toast.error(errorMsg);
      console.error('Error fetching stock details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlist = async () => {
    try {
      const response = await axios.get(`${API}/watchlist`);
      setWatchlist(response.data);
    } catch (error) {
      console.error('Error fetching watchlist:', error);
    }
  };

  const fetchRecommendationList = async () => {
      try {
        const response = await axios.get(`${API}/recommended`);
        setRecommendationList(response.data);
      } catch (error) {
        console.error('Error fetching Recommendation list:', error);
      }
    };

  const fetchMyPositionsList = async () => {
        try {
          let data = {...requestObj};
          const response = await axios.post(`${API}/user/get/portfolio`, data);
          setMyPositionsList(response.data.data.portfolio);
        } catch (error) {
          console.error('Error fetching Positions:', error);
        }
  };

  const addToWatchlist = async () => {
    if (!selectedStock) return;
    
    try {
      await axios.post(`${API}/watchlist`, {
        symbol: selectedStock.symbol,
        name: stockQuote.name
      });
      toast.success('Added to watchlist');
      fetchWatchlist();
      setTab('tab2');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Stock already in watchlist');
      } else {
        toast.error('Failed to add to watchlist');
      }
    }
  };

  const addToMyPositionList = async () => {
      if (!selectedStock) return;

      try {
        let data = {...requestObj};
        data.symbol = selectedStock.symbol;
        await axios.post(`${API}/user/add/portfolio`, data);
        toast.success('Added to position');
        fetchMyPositionsList();
        setTab('tab1');
      } catch (error) {
        if (error.response?.status === 400) {
          toast.error('Stock already in my positions');
        } else {
          toast.error('Failed to add to positions');
        }
      }
    };

  const removeFromWatchlist = async (symbol) => {
    try {
      await axios.delete(`${API}/watchlist/${symbol}`);
      toast.success('Removed from watchlist');
      fetchWatchlist();
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const removeFromMyPosition = async (symbol) => {
    try {
      let data = {...requestObj};
      data.symbol = symbol;
      await axios.post(`${API}/user/delete/portfolio`, data);
      toast.success('Removed from my portfolio');
      fetchMyPositionsList();
    } catch (error) {
      toast.error('Failed to remove from watchlist');
    }
  };

  const changePeriod = async (newPeriod) => {
    if (!selectedStock) return;
    setPeriod(newPeriod);
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stocks/history/${selectedStock.symbol}?period=${newPeriod}`);
      setStockHistory(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to fetch historical data';
      toast.error(errorMsg);
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatLargeNumber = (value) => {
    if (!value) return 'N/A';
    if (value >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    return formatCurrency(value);
  };

  function Login(props) {
      const [username, setUsername] = useState('')
      const [password, setPassword] = useState('')
      const [firstName, setFirstName] = useState('')
      const [lastName, setLastName] = useState('')
      const [phone, setPhone] = useState('')
      const [email, setEmail] = useState('')


      async function handleNewAccount(e) {
          e.preventDefault()
          try {
              const login_response = await axios.post(`${API}/user/register`, {
                username: username,
                password: password,
                firstName: firstName,
                lastName: lastName,
                phone: phone,
                email: email,
                zipcode: 77433,
                address2: "",
                address1: "1",
                city: "Orlando",
                stateId: 1,
                tradingPlatformId: 1
              });
              toast.success('User Registration Successful');
              window.location.reload();
            } catch (error) {
              if (error.response?.status === 400) {
                toast.error('Unable to Register user:' + error.response.data.detail);
              } else {
                toast.error('Unable to Register user' + error.response.data.detail);
              }
          }
          props.toggle()
      }

      async function handleLogin(e) {
          e.preventDefault()
          try {
              const login_response = await axios.post(`${API}/login`, {
                username: username,
                password: password,
                systemIP: '127.0.0.1'
              });
              toast.success('Login Successful');
              
              requestObj = {...login_response.data.data};
              displayName = requestObj['firstName'] + ' ' + requestObj['lastName'];
              delete requestObj['firstName'];
              delete requestObj['lastName'];
              sessionStorage.setItem('token', JSON.stringify(login_response.data.data));
              fetchMyPositionsList();
            } catch (error) {
              if (error.response?.status === 400) {
                toast.error('Unable to Login:' + error.response.data.detail);
              } else {
                toast.error('Unable to Login' + error.response.data.detail);
              }
          }
          props.toggle()
      }

      return (
          <div className="popup">
              <div className="popup-inner">
                 <div className="popup-header-container">
                    <div className="sign-header">Sign Up / Sign In</div>
                    <button className="close-btn" onClick={props.toggle}>X</button>
                  </div>
                  { signIn ?
                  <form onSubmit={handleLogin}>
                      <label>
                          Username: <input type="text" value={username} onChange={e => setUsername(e.target.value)} />
                      </label>
                      <label>
                          Password:
                          <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                      </label>
                      <a href="" className="forgot-password-button">Forgot Password?</a>
                      <button type="submit">Login</button>
                      <div >New to Stock Catcher? <a className="create-account-button" onClick={toggleSignIn} href="#">Create Account</a></div>
                  </form> :
                  
                  <form onSubmit={handleNewAccount}>
                      <label>
                          Username: <input type="text" value={username} onChange={e => setUsername(e.target.value)} />
                      </label>
                      <label>
                          Password:
                          <input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                      </label>
                      <label>
                          First Name: <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} />
                      </label>
                      <label>
                          Last Name: <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} />
                      </label>
                      <label>
                          Phone: <input type="phone" value={phone} onChange={e => setPhone(e.target.value)} />
                      </label>
                      <label>
                          Email: <input type="email" value={email} onChange={e => setEmail(e.target.value)} />
                      </label>
                      <button type="submit">Create Account</button>
                      <div >Already a member? <a className="create-account-button" onClick={toggleSignIn} href="#">Login</a></div>
                  </form>}
                  
              </div>
          </div>
      )
  }

  async function logout(e) {
    e.preventDefault();
    try {
      const logout_response = await axios.post(`${API}/logout`, requestObj);
      toast.success('Login Successful');
      sessionStorage.removeItem('token');
      window.location.reload();
      toast.success('Logout Successful');
      fetchMyPositionsList();  
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Unable to Logout:' + error.response.data.detail);
      } else {
        toast.error('Unable to Logout' + error.response.data.detail);
      }
    }
  }

  const renderChart = () => {
    if (!stockHistory.length) return null;

    const data = stockHistory.map(item => ({
      date: item.date,
      price: item.close,
      open: item.open,
      high: item.high,
      low: item.low,
      volume: item.volume
    }));

    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Area type="monotone" dataKey="price" stroke="#3b82f6" fillOpacity={1} fill="url(#colorPrice)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        );
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => formatLargeNumber(value)}
              />
              <Legend />
              <Bar dataKey="volume" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', color: '#fff' }}
                formatter={(value) => formatCurrency(value)}
              />
              <Legend />
              <Line type="monotone" dataKey="high" stroke="#10b981" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="low" stroke="#ef4444" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="price" stroke="#3b82f6" strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <div className="app-container">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <TrendingUp className="logo-icon" />
            <h1 className="logo-text">Stock Catcher</h1>
          </div>
          
          <div className="search-container">
            <Search className="search-icon" />
            <Input
              data-testid="stock-search-input"
              type="text"
              placeholder="Search stocks (e.g., AAPL, GOOGL, TSLA)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchResults.length > 0 && (
              <div className="search-results" data-testid="search-results">
                {searchResults.map((stock) => (
                  <div
                    key={stock.symbol}
                    data-testid={`search-result-${stock.symbol}`}
                    className="search-result-item"
                    onClick={() => fetchStockDetails(stock.symbol, stock.name)}
                  >
                    <div className="stock-symbol">{stock.symbol}</div>
                    <div className="stock-name">{stock.name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {displayName ? 
            <div className="signup-div">
              <label className='display-name'>{displayName}</label>
              <span>|</span>
              <a className="sign-up" onClick={logout} href="#">Logout</a>
            </div> :
            <div className="signup-div">
              <a className="sign-in" onClick={togglePop} href="#">SIGN IN</a>
              {seen ? <Login toggle={togglePop} /> : null}
              <span>|</span>
              <a className="sign-up" onClick={togglePop} href="#">CREATE ACCOUNT</a>
            </div>}
        </div>
      </header>

      <main className="main-content">
        <div className="content-grid">
          <div className="left-panel">
            {selectedStock && stockQuote ? (
              <div className="stock-details" data-testid="stock-details">
                <Card className="quote-card">
                  <CardHeader>
                    <div className="quote-header">
                      <div>
                        <CardTitle className="stock-title" data-testid="stock-name">{stockQuote.name}</CardTitle>
                        <CardDescription data-testid="stock-symbol">{stockQuote.symbol}</CardDescription>
                      </div>
                       <Button
                          data-testid="add-to-watchlist-btn"
                          onClick={addToMyPositionList}
                          size="sm"
                          className="add-my-position-btn"
                        >
                          <Plus className="w-4 h-4" />
                          Add To Position
                        </Button>
                      <Button
                        data-testid="add-to-watchlist-btn"
                        onClick={addToWatchlist}
                        size="sm"
                        className="add-watchlist-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Watchlist
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="price-section">
                          {stockQuote.post_market_quote ?
                          <div>
                                  <label className="current-price">After Market</label>
                                  <div className="current-price" data-testid="stock-price">{formatCurrency(stockQuote.post_market_quote?.price)}</div>
                                  <div className={`price-change ${stockQuote.post_market_quote?.change >= 0 ? 'positive' : 'negative'}`} data-testid="stock-change">
                                    {stockQuote.post_market_quote?.change >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                    {formatCurrency(Math.abs(stockQuote.post_market_quote.change))} ({Math.abs(stockQuote.post_market_quote?.change_percent)}%)
                                  </div>
                                  <hr className="hrClass"></hr>
                                 <label className="current-price">At Market</label>
                                 <div className="current-price" data-testid="stock-price">{formatCurrency(stockQuote.price)}</div>
                                 <div className={`price-change ${stockQuote.change >= 0 ? 'positive' : 'negative'}`} data-testid="stock-change">
                                  {stockQuote.change >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                  {formatCurrency(Math.abs(stockQuote.change))} ({Math.abs(stockQuote.change_percent)}%)
                                 </div>
                            </div>
                            :
                            <div>
                                 <label className="current-price">At Market</label>
                                 <div className="current-price" data-testid="stock-price">{formatCurrency(stockQuote.price)}</div>
                                 <div className={`price-change ${stockQuote.change >= 0 ? 'positive' : 'negative'}`} data-testid="stock-change">
                                  {stockQuote.change >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                                  {formatCurrency(Math.abs(stockQuote.change))} ({Math.abs(stockQuote.change_percent)}%)
                                 </div>
                            </div>
                            }
                    </div>

                    <div className="stats-grid">
                      <div className="stat-item">
                        <div className="stat-label">Open</div>
                        <div className="stat-value">{formatCurrency(stockQuote.open_price || 0)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">High</div>
                        <div className="stat-value">{formatCurrency(stockQuote.day_high || 0)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Low</div>
                        <div className="stat-value">{formatCurrency(stockQuote.day_low || 0)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Prev Close</div>
                        <div className="stat-value">{formatCurrency(stockQuote.previous_close || 0)}</div>
                      </div>
                      <div className="stat-item">
                          <div className="stat-label">Volume</div>
                          <div className="stat-value">{formatLargeNumber(stockQuote.volume)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Market Cap</div>
                        <div className="stat-value">{formatLargeNumber(stockQuote.market_cap)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">P/E Ratio</div>
                        <div className="stat-value">{stockQuote.pe_ratio?.toFixed(2) || 'N/A'}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">52 Week High</div>
                        <div className="stat-value">{formatCurrency(stockQuote.fifty_two_week_high || 0)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">52 Week Low</div>
                        <div className="stat-value">{formatCurrency(stockQuote.fifty_two_week_low || 0)}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Recommendation</div>
                        <div className="stat-value">{stockQuote.recommendation || 'N/A'}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">Analysts Rated</div>
                        <div className="stat-value">{stockQuote.num_of_analysts || 0}</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-label">EPS</div>
                        <div className="stat-value">{stockQuote.eps || 0}</div>
                      </div>
                      
                      
                    </div>
                  </CardContent>
                </Card>

                <Card className="chart-card">
                  <CardHeader>
                    <div className="chart-header">
                      <CardTitle>Price Chart</CardTitle>
                      <div className="chart-controls">
                        <div className="period-buttons">
                          {['1d', '5d', '1mo', '3mo', '6mo', '1y'].map((p) => (
                            <Button
                              key={p}
                              data-testid={`period-${p}`}
                              onClick={() => changePeriod(p)}
                              variant={period === p ? 'default' : 'outline'}
                              size="sm"
                              className="period-btn"
                            >
                              {p.toUpperCase()}
                            </Button>
                          ))}
                        </div>
                        <div className="chart-type-buttons">
                          <Button
                            data-testid="chart-type-line"
                            onClick={() => setChartType('line')}
                            variant={chartType === 'line' ? 'default' : 'outline'}
                            size="sm"
                          >
                            <LineChartIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid="chart-type-area"
                            onClick={() => setChartType('area')}
                            variant={chartType === 'area' ? 'default' : 'outline'}
                            size="sm"
                          >
                            <AreaChart className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid="chart-type-bar"
                            onClick={() => setChartType('bar')}
                            variant={chartType === 'bar' ? 'default' : 'outline'}
                            size="sm"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="loading-state">Loading chart data...</div>
                    ) : (
                      <div data-testid="stock-chart">{renderChart()}</div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="empty-state" data-testid="empty-state">
                <TrendingUp className="empty-icon" />
                <h2 className="empty-title">Search for a Stock</h2>
                <p className="empty-description">
                  Enter a stock symbol or company name in the search bar above to view detailed information and charts.
                </p>
              </div>
            )}
          </div>

          <div className="right-panel">
          <Tabs value={tab} onValueChange={onTabChange}>
            <TabsList>
               <TabsTrigger className="tab-trigger" onClick={fetchMyPositionsList} value="tab1">My Positions</TabsTrigger>
               <TabsTrigger className="tab-trigger" value="tab2">Watchlist</TabsTrigger>
               <TabsTrigger className="tab-trigger" value="tab3">Recommendation</TabsTrigger>
            </TabsList>
            <TabsContent value="tab1">
                <Card className="watchlist-card">
                  <CardHeader>
                    <CardTitle>My Positions</CardTitle>
                    <CardDescription>My tracked stocks</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {myPositionsList.length > 0 ? (
                      <div className="watchlist-items" data-testid="watchlist">
                        {myPositionsList.map((stock) => (
                          <div
                            key={stock.symbol}
                            data-testid={`watchlist-item-${stock.symbol}`}
                            className="watchlist-item"
                          >
                            <div
                              className="watchlist-info"
                              onClick={() => fetchStockDetails(stock.symbol, stock.name)}
                            >
                              <div className="watchlist-symbol">{stock.symbol}</div>
                              <div className="watchlist-name">{stock.name}</div>
                            </div>
                            <Button
                              data-testid={`remove-watchlist-${stock.symbol}`}
                              onClick={() => removeFromMyPosition(stock.symbol)}
                              variant="ghost"
                              size="sm"
                              className="remove-btn"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="watchlist-empty" data-testid="watchlist-empty">
                        <p>No stocks in your watchlist yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
            </TabsContent>
            <TabsContent value="tab2">
                <Card className="watchlist-card">
                  <CardHeader>
                    <CardTitle>Watchlist</CardTitle>
                    <CardDescription>Your tracked stocks</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {watchlist.length > 0 ? (
                      <div className="watchlist-items" data-testid="watchlist">
                        {watchlist.map((stock) => (
                          <div
                            key={stock.symbol}
                            data-testid={`watchlist-item-${stock.symbol}`}
                            className="watchlist-item"
                          >
                            <div
                              className="watchlist-info"
                              onClick={() => fetchStockDetails(stock.symbol, stock.name)}
                            >
                              <div className="watchlist-symbol">{stock.symbol}</div>
                              <div className="watchlist-name">{stock.name}</div>
                            </div>
                            <Button
                              data-testid={`remove-watchlist-${stock.symbol}`}
                              onClick={() => removeFromWatchlist(stock.symbol)}
                              variant="ghost"
                              size="sm"
                              className="remove-btn"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="watchlist-empty" data-testid="watchlist-empty">
                        <p>No stocks in your watchlist yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
            </TabsContent>
            <TabsContent value="tab3">
                <Card className="watchlist-card">
                  <CardHeader>
                    <CardTitle>Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {recommendationList.length > 0 ? (
                      <div className="watchlist-items" data-testid="watchlist">
                        {recommendationList.map((stock) => (
                          <div
                            key={stock.symbol}
                            data-testid={`watchlist-item-${stock.symbol}`}
                            className="watchlist-item"
                          >
                            <div
                              className="watchlist-info"
                              onClick={() => fetchStockDetails(stock.symbol, stock.name)}
                            >
                                <div className="display-flex">
                                      <div>
                                        <div className="watchlist-symbol">{stock.symbol}</div>
                                        <div className="watchlist-name">{stock.name}</div>
                                      </div>
                                      <div className="recommendation-list-rank margin">{stock.recommendation}</div>
                                </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="watchlist-empty" data-testid="watchlist-empty">
                        <p>No stocks in Recommendation List yet.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
            </TabsContent>

            </Tabs>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;